// CelebrationBodyOverlay.js
// A small, mixer-independent additive body layer for the level/course
// celebration. The owner must call remove() before mixer.update(), followed by
// apply() afterwards. This ordering also makes unkeyed channels reversible.

import {
    Box3,
    Matrix4,
    Quaternion,
    Vector3,
} from "three";

const EXCLUDED_NAME = /(twist|helper|ik|pole|target|ctrl|control|socket|offset|nub|end)/i;
const IDENTITY = new Quaternion();
const EPSILON = 1e-6;

// Earlier aliases win. Matching is punctuation/case insensitive, but never
// fuzzy: an ambiguous alias is deliberately reported rather than guessed.
const ALIASES = Object.freeze({
    leftUpperArm: ["Shoulder_L", "LeftUpperArm", "mixamorigLeftArm", "UpperArm_L", "Arm_L"],
    rightUpperArm: ["Shoulder_R", "RightUpperArm", "mixamorigRightArm", "UpperArm_R", "Arm_R"],
    leftElbow: ["Elbow_L", "LeftForeArm", "mixamorigLeftForeArm", "ForeArm_L", "LowerArm_L"],
    rightElbow: ["Elbow_R", "RightForeArm", "mixamorigRightForeArm", "ForeArm_R", "LowerArm_R"],
    leftWrist: ["Wrist_L", "LeftHand", "mixamorigLeftHand", "Hand_L"],
    rightWrist: ["Wrist_R", "RightHand", "mixamorigRightHand", "Hand_R"],
    neck: ["Neck_M", "Neck", "mixamorigNeck", "Bip01_Neck"],
    head: ["Head_M", "Head", "mixamorigHead", "Bip01_Head"],
    leftEye: ["EyeJoint_L", "Eye_L", "LeftEye", "mixamorigLeftEye"],
    rightEye: ["EyeJoint_R", "Eye_R", "RightEye", "mixamorigRightEye"],
    spineLower: ["Spine1Part1_M", "Spine1Part1", "Spine1_M", "Spine1", "mixamorigSpine", "Spine"],
    spineUpper: ["Spine1Part2_M", "Spine1Part2", "Spine2_M", "Spine2", "mixamorigSpine1", "mixamorigSpine2", "Chest", "UpperChest"],
    hips: ["Hips_M", "Hips", "Pelvis_M", "Pelvis", "mixamorigHips", "Root_M"],
});

const _a = new Vector3();
const _b = new Vector3();
const _c = new Vector3();
const _up = new Vector3();
const _right = new Vector3();
const _forward = new Vector3();
const _towardCamera = new Vector3();
const _headForward = new Vector3();
const _headUp = new Vector3();
const _eyeRight = new Vector3();
const _eyeMidpoint = new Vector3();
const _palmNormal = new Vector3();
const _direction = new Vector3();
const _target = new Vector3();
const _parentWorldQ = new Quaternion();
const _desiredWorldQ = new Quaternion();
const _localQ = new Quaternion();
const _weightedQ = new Quaternion();
const _inverseParent = new Matrix4();

function normalizedName(name) {
    return String(name || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
}

function isDescendant(node, ancestor) {
    for (let cursor = node?.parent; cursor; cursor = cursor.parent) {
        if (cursor === ancestor) return true;
    }
    return false;
}

function nodePosition(node, target) {
    return node.getWorldPosition(target);
}

function finiteVector(vector) {
    return Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
}

function firstUsefulChild(node) {
    const queue = [...(node?.children || [])];
    while (queue.length) {
        const child = queue.shift();
        if (!EXCLUDED_NAME.test(child.name || "") && child.isBone) return child;
        queue.push(...(child.children || []));
    }
    return null;
}

/**
 * Procedural open-arms body contribution.
 *
 * Lifecycle per rendered frame:
 *   overlay.remove(); mixer.update(delta); overlay.apply(weight, tier);
 *
 * This class never changes the model root, model scale, camera or controls.
 */
export class CelebrationBodyOverlay {
    constructor(viewer) {
        this.viewer = viewer;
        this.model = null;
        this.channels = Object.create(null);
        this._contributions = [];
        this._coverage = this._emptyReport("not-resolved");
        this._disposed = false;
        this._headForwardLocal = new Vector3();
    }

    get coverageReport() {
        return this._coverage;
    }

    // Concise compatibility getter for diagnostics which call overlay.coverage.
    get coverage() {
        return this._coverage;
    }

    _emptyReport(reason) {
        return {
            model: null,
            supported: false,
            tiers: { level: false, course: false },
            channels: {},
            constraints: {
                additive: true,
                mixerOrder: "remove -> mixer.update -> apply",
                faceMorphWrites: false,
                silentWindowHeadBoneAim: true,
                cameraMutation: false,
                modelRootOrScaleMutation: false,
                shiftHumanMeters: { level: 0.025, course: 0.035 },
            },
            reason,
        };
    }

    /**
     * Resolves exact semantic aliases and validates the limb/body hierarchy.
     * Returns the coverage report; unsupported rigs remain safe no-ops.
     */
    resolve() {
        if (this._disposed) return this._coverage;
        const model = this.viewer?.model || null;
        // A diagnostic resolve on the displayed model is observational: do not
        // remove a live post-mixer contribution merely to return cached data.
        if (model && model === this.model && Object.keys(this.channels).length) {
            return this._coverage;
        }
        this.remove();
        this.model = model;
        this.channels = Object.create(null);
        if (!model?.traverse) {
            this._coverage = this._emptyReport("model-unavailable");
            return this._coverage;
        }

        model.updateMatrixWorld?.(true);
        const eligible = [];
        model.traverse((node) => {
            if (!node || EXCLUDED_NAME.test(node.name || "")) return;
            // Restrict rotations/translations to bones. Scene/model roots are
            // intentionally ineligible, preventing permanent placement edits.
            if (node.isBone) eligible.push(node);
        });

        const status = {};
        for (const [semantic, aliases] of Object.entries(ALIASES)) {
            let result = null;
            let reason = "missing";
            for (const alias of aliases) {
                const key = normalizedName(alias);
                const matches = eligible.filter((node) => normalizedName(node.name) === key);
                if (matches.length === 1) {
                    result = matches[0];
                    reason = null;
                    break;
                }
                if (matches.length > 1) {
                    reason = `ambiguous:${alias}`;
                    break;
                }
            }
            this.channels[semantic] = result;
            status[semantic] = result
                ? { supported: true, node: result.name }
                : { supported: false, reason };
        }

        this._validatePair("leftUpperArm", "leftElbow", status);
        this._validatePair("rightUpperArm", "rightElbow", status);
        this._validatePair("leftElbow", "leftWrist", status, true);
        this._validatePair("rightElbow", "rightWrist", status, true);
        this._validatePair("hips", "spineLower", status, true);
        this._validatePair("spineLower", "spineUpper", status, true);
        this._validatePair("spineUpper", "neck", status, true);
        this._validatePair("neck", "head", status, true);
        this._validatePair("spineUpper", "head", status, true);
        this._validatePair("head", "leftEye", status, true);
        this._validatePair("head", "rightEye", status, true);
        this._validateUnique(status);

        const leftPalm = this._handGuide(this.channels.leftWrist, _a);
        const rightPalm = this._handGuide(this.channels.rightWrist, _a);
        const leftPalmOffer = this._hasPalmPlane(this.channels.leftWrist);
        const rightPalmOffer = this._hasPalmPlane(this.channels.rightWrist);
        status.leftPalmGeometry = leftPalm
            ? { supported: true, source: "semantic-finger-centroid" }
            : { supported: false, reason: "missing-semantic-finger-geometry" };
        status.rightPalmGeometry = rightPalm
            ? { supported: true, source: "semantic-finger-centroid" }
            : { supported: false, reason: "missing-semantic-finger-geometry" };

        const arms = Boolean(
            this.channels.leftUpperArm && this.channels.leftElbow &&
            this.channels.rightUpperArm && this.channels.rightElbow
        );
        const torso = Boolean(this.channels.spineLower || this.channels.spineUpper);
        const geometryReason = arms && torso ? this._deriveAnatomicalFrame() : null;
        const geometry = !geometryReason;
        const headGazeReason = geometry ? this._calibrateHeadGaze() : "body-geometry-unavailable";
        status.headGazeGeometry = headGazeReason
            ? { supported: false, reason: headGazeReason }
            : { supported: true, source: "eye-line-and-neck-head-up" };
        const shift = Boolean(
            this.channels.hips && this.viewer?.camera && geometry &&
            _towardCamera.lengthSq() > EPSILON
        );
        const supported = arms && torso && geometry;
        this._coverage = {
            model: model.name || null,
            supported,
            tiers: { level: supported, course: supported },
            channels: status,
            capabilities: {
                pairedArms: arms,
                torso,
                anatomicalGeometry: geometry,
                cameraDirectedShift: shift,
                palms: { left: leftPalm, right: rightPalm },
                palmOfferRoll: { left: leftPalmOffer, right: rightPalmOffer },
                cameraHeadAim: Boolean(!headGazeReason && this.viewer?.camera),
            },
            constraints: this._emptyReport().constraints,
            headAim: headGazeReason
                ? { supported: false, reason: headGazeReason }
                : {
                    supported: true,
                    calibration: "eye-line-and-neck-head-up",
                    lastSampleDegrees: null,
                    lastHoldDegrees: null,
                },
            reason: supported ? null : (geometryReason || "paired-arms-and-torso-required"),
        };
        return this._coverage;
    }

    _validatePair(parentKey, childKey, status, optionalChild = false) {
        const parent = this.channels[parentKey];
        const child = this.channels[childKey];
        if (!child && optionalChild) return;
        if (!parent || !child || parent === child || !isDescendant(child, parent)) {
            if (child) {
                status[childKey] = { supported: false, reason: `invalid-hierarchy:${parentKey}` };
                this.channels[childKey] = null;
            }
        }
    }

    _validateUnique(status) {
        const ownerByNode = new Map();
        for (const semantic of Object.keys(ALIASES)) {
            const node = this.channels[semantic];
            if (!node) continue;
            const owner = ownerByNode.get(node);
            if (owner) {
                status[semantic] = { supported: false, reason: `duplicate-of:${owner}` };
                this.channels[semantic] = null;
            } else {
                ownerByNode.set(node, semantic);
            }
        }
    }

    /**
     * Removes exactly the local deltas applied on the preceding frame.
     * Call before the animation mixer, including on interruption/disposal.
     */
    remove() {
        for (let i = this._contributions.length - 1; i >= 0; i -= 1) {
            const contribution = this._contributions[i];
            const node = contribution.node;
            if (!node) continue;
            if (contribution.rotation) {
                node.quaternion.multiply(contribution.rotation).normalize();
            }
            if (contribution.position) node.position.sub(contribution.position);
        }
        this._contributions.length = 0;
        this.model?.updateMatrixWorld?.(true);
    }

    /**
     * Applies a contribution over the current (post-mixer) pose.
     * `weight` is the caller's eased 0..1 envelope. Lesson/unknown tiers no-op.
     */
    apply(weight, tier, phase = null) {
        if (this._disposed) return false;
        if (this._contributions.length) this.remove();
        if (tier !== "level" && tier !== "course") return false;

        const model = this.viewer?.model || null;
        if (model !== this.model || !Object.keys(this.channels).length) this.resolve();
        if (!this._coverage.supported || model !== this.model) return false;

        const amount = Math.max(0, Math.min(1.12, Number(weight) || 0));
        if (amount <= EPSILON) return false;
        const size = tier === "course" ? 1.22 : 1;
        model.updateMatrixWorld?.(true);

        if (this._deriveAnatomicalFrame()) return false;

        this._openArm(this.channels.leftUpperArm, this.channels.leftElbow, -1, amount, size);
        this._openArm(this.channels.rightUpperArm, this.channels.rightElbow, 1, amount, size);
        this._openElbow(this.channels.leftElbow, this.channels.leftWrist, -1, amount, size);
        this._openElbow(this.channels.rightElbow, this.channels.rightWrist, 1, amount, size);
        this._openPalm(this.channels.leftWrist, -1, amount, size);
        this._openPalm(this.channels.rightWrist, 1, amount, size);
        this._offerPalm(this.channels.leftWrist, -1, amount);
        this._offerPalm(this.channels.rightWrist, 1, amount);

        // A chest lift around the measured shoulder axis, never a head/face write.
        const chestAngle = (tier === "course" ? 0.06 : 0.045) * amount;
        if (this.channels.spineLower) {
            this._applyWorldAxisRotation(this.channels.spineLower, _right, chestAngle * 0.38);
        }
        if (this.channels.spineUpper) {
            this._applyWorldAxisRotation(this.channels.spineUpper, _right, chestAngle * 0.62);
        }
        // Include the just-applied chest lift when measuring current head gaze.
        model.updateMatrixWorld?.(true);
        this._aimHead(amount, tier, phase);

        if (this._coverage.capabilities.cameraDirectedShift) {
            this._applyForwardShift(amount, tier);
        }
        model.updateMatrixWorld?.(true);
        return true;
    }

    _deriveAnatomicalFrame() {
        const left = this.channels.leftUpperArm;
        const right = this.channels.rightUpperArm;
        const lower = this.channels.spineLower || this.channels.hips;
        const upper = this.channels.spineUpper || this.channels.leftUpperArm;
        _towardCamera.set(0, 0, 0);
        if (!left || !right || !lower || !upper) return "geometry:missing-landmarks";

        nodePosition(left, _a);
        nodePosition(right, _b);
        _right.subVectors(_b, _a);
        nodePosition(lower, _a);
        nodePosition(upper, _b);
        _up.subVectors(_b, _a);
        if (_right.lengthSq() < EPSILON) return "geometry:coincident-shoulders";
        if (_up.lengthSq() < EPSILON) return "geometry:zero-torso";
        _right.normalize();
        // Gram-Schmidt keeps malformed/leaning rigs from producing a skew axis.
        _up.addScaledVector(_right, -_up.dot(_right));
        if (_up.lengthSq() < EPSILON) return "geometry:collinear-torso-and-shoulders";
        _up.normalize();
        _forward.crossVectors(_right, _up).normalize();

        const camera = this.viewer?.camera;
        if (camera) {
            nodePosition(this.channels.hips || lower, _a);
            camera.getWorldPosition?.(_b) || _b.copy(camera.position);
            _direction.subVectors(_b, _a);
            _direction.addScaledVector(_up, -_direction.dot(_up));
            if (_direction.lengthSq() > EPSILON) {
                _towardCamera.copy(_direction).normalize();
                if (_forward.dot(_towardCamera) < 0) _forward.negate();
            }
        }
        return finiteVector(_forward) ? null : "geometry:non-finite-axis";
    }

    _calibrateHeadGaze() {
        const { neck, head, leftEye, rightEye } = this.channels;
        this._headForwardLocal.set(0, 0, 0);
        if (!neck || !head || !leftEye || !rightEye) return "missing-head-eye-landmarks";

        nodePosition(leftEye, _a);
        nodePosition(rightEye, _b);
        _eyeRight.subVectors(_b, _a);
        if (_eyeRight.lengthSq() < EPSILON) return "coincident-eyes";
        _eyeRight.normalize();
        // Reject swapped semantic sides rather than silently aiming backwards.
        if (_eyeRight.dot(_right) < 0.5) return "reversed-or-skewed-eye-line";

        nodePosition(neck, _a);
        nodePosition(head, _b);
        _headUp.subVectors(_b, _a);
        _headUp.addScaledVector(_eyeRight, -_headUp.dot(_eyeRight));
        if (_headUp.lengthSq() < EPSILON) return "degenerate-head-up";
        _headUp.normalize();

        _headForward.crossVectors(_eyeRight, _headUp).normalize();
        if (_headForward.dot(_forward) < 0) _headForward.negate();
        head.getWorldQuaternion(_parentWorldQ);
        this._headForwardLocal.copy(_headForward)
            .applyQuaternion(_parentWorldQ.invert())
            .normalize();
        return finiteVector(this._headForwardLocal) ? null : "non-finite-head-basis";
    }

    _openArm(arm, elbow, side, weight, size) {
        if (!arm || !elbow) return;
        nodePosition(arm, _a);
        nodePosition(elbow, _b);
        _direction.subVectors(_b, _a);
        if (_direction.lengthSq() < EPSILON) return;
        _direction.normalize();

        // Side is -1 left/+1 right because _right points left shoulder -> right.
        _target.copy(_right).multiplyScalar(side * 0.8)
            .addScaledVector(_up, -0.4)
            .addScaledVector(_forward, 0.35)
            .normalize();
        _desiredWorldQ.setFromUnitVectors(_direction, _target);
        const desiredAngle = _desiredWorldQ.angleTo(IDENTITY);
        if (desiredAngle <= EPSILON) return;
        // Scale a stable pose delta rather than capping its angle. This remains
        // smooth through overshoot and return portions of the caller envelope.
        const fraction = (size > 1 ? 0.44 : 0.35) * weight;
        _weightedQ.copy(IDENTITY).slerp(_desiredWorldQ, fraction);
        this._applyWorldRotation(arm, _weightedQ);
    }

    _openElbow(elbow, wrist, side, weight, size) {
        if (!elbow) return;
        const end = wrist || firstUsefulChild(elbow);
        if (!end) return;
        nodePosition(elbow, _a);
        nodePosition(end, _b);
        _direction.subVectors(_b, _a);
        if (_direction.lengthSq() < EPSILON) return;
        _direction.normalize();
        _target.copy(_right).multiplyScalar(side * 0.65)
            .addScaledVector(_up, -0.3)
            .addScaledVector(_forward, 0.7)
            .normalize();
        _desiredWorldQ.setFromUnitVectors(_direction, _target);
        const desiredAngle = _desiredWorldQ.angleTo(IDENTITY);
        if (desiredAngle <= EPSILON) return;
        const fraction = (size > 1 ? 0.32 : 0.25) * weight;
        _weightedQ.copy(IDENTITY).slerp(_desiredWorldQ, fraction);
        this._applyWorldRotation(elbow, _weightedQ);
    }

    _handGuide(wrist, target) {
        if (!wrist) return false;
        let count = 0;
        target.set(0, 0, 0);
        // A centroid across named finger/thumb roots is stable across differing
        // finger counts and avoids guessing one child when several are present.
        for (const child of wrist.children || []) {
            const name = child.name || "";
            if (
                child.isBone &&
                !EXCLUDED_NAME.test(name) &&
                /(finger|thumb|index|middle|ring|pinky|little)/i.test(name)
            ) {
                target.add(nodePosition(child, _c));
                count += 1;
            }
        }
        if (!count) return false;
        target.multiplyScalar(1 / count);
        return finiteVector(target);
    }

    _fingerRoot(wrist, expression) {
        if (!wrist) return null;
        const matches = (wrist.children || []).filter((child) => (
            child.isBone &&
            !EXCLUDED_NAME.test(child.name || "") &&
            expression.test(child.name || "")
        ));
        // Multiple roots of one semantic type are not safe enough to establish
        // a palm plane; do not select by traversal order.
        return matches.length === 1 ? matches[0] : null;
    }

    _palmPlaneLandmarks(wrist) {
        const thumb = this._fingerRoot(wrist, /thumb/i);
        const index = this._fingerRoot(wrist, /index/i);
        const pinky = this._fingerRoot(wrist, /(pinky|little)/i);
        return thumb && index && pinky ? { thumb, index, pinky } : null;
    }

    _hasPalmPlane(wrist) {
        const landmarks = this._palmPlaneLandmarks(wrist);
        if (!landmarks) return false;
        nodePosition(landmarks.thumb, _a);
        nodePosition(landmarks.index, _b);
        nodePosition(landmarks.pinky, _c);
        _b.sub(_a);
        _c.sub(_a);
        return _b.cross(_c).lengthSq() > EPSILON;
    }

    _openPalm(wrist, side, weight, size) {
        if (!wrist || !this._handGuide(wrist, _b)) return;
        nodePosition(wrist, _a);
        _direction.subVectors(_b, _a);
        if (_direction.lengthSq() < EPSILON) return;
        _direction.normalize();
        // Preserve the authored palm direction, adding only a small welcoming
        // outward/upward reveal derived from body-world anatomy.
        _target.copy(_direction)
            .addScaledVector(_right, side * 0.22)
            .addScaledVector(_up, 0.16)
            .addScaledVector(_forward, 0.12)
            .normalize();
        _desiredWorldQ.setFromUnitVectors(_direction, _target);
        const desiredAngle = _desiredWorldQ.angleTo(IDENTITY);
        if (desiredAngle <= EPSILON) return;
        const baseFraction = size > 1 ? 0.54 : 0.42;
        const maxAngle = Math.PI / 15; // 12 degrees, including unusual hand rigs.
        const fraction = Math.min(baseFraction, maxAngle / desiredAngle)
            * Math.min(weight, 1);
        _weightedQ.copy(IDENTITY).slerp(_desiredWorldQ, fraction);
        this._applyWorldRotation(wrist, _weightedQ);
    }

    _offerPalm(wrist, side, weight) {
        const landmarks = this._palmPlaneLandmarks(wrist);
        if (!wrist || !landmarks || _towardCamera.lengthSq() <= EPSILON) return;
        if (!this._handGuide(wrist, _b)) return;
        nodePosition(wrist, _a);
        _direction.subVectors(_b, _a);
        if (_direction.lengthSq() <= EPSILON) return;
        _direction.normalize(); // measured hand long axis

        nodePosition(landmarks.thumb, _a);
        nodePosition(landmarks.index, _b);
        nodePosition(landmarks.pinky, _c);
        _b.sub(_a);
        _c.sub(_a);
        _palmNormal.crossVectors(_b, _c).multiplyScalar(-side);
        _palmNormal.addScaledVector(_direction, -_palmNormal.dot(_direction));
        if (_palmNormal.lengthSq() <= EPSILON) return;
        _palmNormal.normalize();

        // Roll only: compare palm normal with learner direction in the plane
        // perpendicular to the measured hand axis.
        _target.copy(_towardCamera)
            .addScaledVector(_direction, -_towardCamera.dot(_direction));
        if (_target.lengthSq() <= EPSILON) return;
        _target.normalize();
        _c.crossVectors(_palmNormal, _target);
        const signedError = Math.atan2(
            _direction.dot(_c),
            Math.max(-1, Math.min(1, _palmNormal.dot(_target))),
        );
        const maxRoll = Math.PI / 18; // restrained 10-degree supination.
        const roll = Math.max(-maxRoll, Math.min(maxRoll, signedError))
            * Math.min(weight, 1);
        this._applyWorldAxisRotation(wrist, _direction, roll);
    }

    _aimHead(weight, tier, phase) {
        const { head, leftEye, rightEye } = this.channels;
        const camera = this.viewer?.camera;
        if (
            !head || !leftEye || !rightEye || !camera ||
            this._headForwardLocal.lengthSq() <= EPSILON
        ) return;

        // Transform the neutral-calibrated gaze plane by the current post-mixer
        // head pose. Eye midpoint is only the target origin; it is intentionally
        // not treated as a gaze axis (eyes naturally sit above the head pivot).
        head.getWorldQuaternion(_parentWorldQ);
        _headForward.copy(this._headForwardLocal)
            .applyQuaternion(_parentWorldQ)
            .normalize();
        nodePosition(leftEye, _a);
        nodePosition(rightEye, _b);
        _eyeMidpoint.addVectors(_a, _b).multiplyScalar(0.5);
        camera.getWorldPosition?.(_target) || _target.copy(camera.position);
        _direction.subVectors(_target, _eyeMidpoint);
        if (_direction.lengthSq() <= EPSILON) return;
        _direction.normalize();

        _desiredWorldQ.setFromUnitVectors(_headForward, _direction);
        const desiredAngle = _desiredWorldQ.angleTo(IDENTITY);
        if (desiredAngle <= EPSILON) return;
        // Correct fully when nearby, otherwise retain a suit-safe bounded turn.
        // Keep the unweighted bounded capability separate from what this frame's
        // envelope actually applies. In particular, release samples must not
        // masquerade as evidence of the full-weight hold.
        const maxAngle = tier === "course" ? Math.PI * 14 / 180 : Math.PI / 15;
        const cappedAngle = Math.min(desiredAngle, maxAngle);
        const appliedAngle = cappedAngle * Math.min(weight, 1);
        const fraction = appliedAngle / desiredAngle;
        if (this._coverage.headAim?.supported) {
            const sample = {
                phase,
                weight,
                inputError: desiredAngle * 180 / Math.PI,
                unweightedCappedCorrection: cappedAngle * 180 / Math.PI,
                actualAppliedCorrection: appliedAngle * 180 / Math.PI,
                residualEstimate: Math.max(0, desiredAngle - appliedAngle) * 180 / Math.PI,
                correctionLimit: maxAngle * 180 / Math.PI,
            };
            this._coverage.headAim.lastSampleDegrees = sample;
            if (phase === "hold") {
                this._coverage.headAim.lastHoldDegrees = { ...sample };
            }
        }
        _weightedQ.copy(IDENTITY).slerp(_desiredWorldQ, fraction);
        this._applyWorldRotation(head, _weightedQ);
    }

    _applyWorldAxisRotation(node, axis, angle) {
        if (!node || Math.abs(angle) <= EPSILON) return;
        _desiredWorldQ.setFromAxisAngle(axis, angle);
        this._applyWorldRotation(node, _desiredWorldQ);
    }

    _applyWorldRotation(node, worldDelta) {
        node.parent?.getWorldQuaternion(_parentWorldQ);
        if (!node.parent) _parentWorldQ.identity();
        // qLocal' = parentWorld^-1 * worldDelta * parentWorld * qLocal
        _localQ.copy(_parentWorldQ).invert()
            .multiply(worldDelta)
            .multiply(_parentWorldQ)
            .normalize();
        const before = node.quaternion.clone();
        node.quaternion.premultiply(_localQ).normalize();
        // A^-1 B is a right-side correction from rendered A to prior B. If a
        // later writer premultiplies E, (E A)(A^-1 B) = E B.
        const correction = node.quaternion.clone().invert().multiply(before);
        this._contributions.push({ node, rotation: correction, position: null });
    }

    _applyForwardShift(weight, tier) {
        const hips = this.channels.hips;
        if (!hips?.parent) return;

        // Infer authored-world scale from anatomical measurements, falling back
        // to the visible model height. Thus 0.025 means ~2.5 human centimetres
        // whether a rig is authored in metres, centimetres, or parent-scaled.
        nodePosition(this.channels.leftUpperArm, _a);
        nodePosition(this.channels.rightUpperArm, _b);
        let authoredScale = _a.distanceTo(_b) / 0.38;
        if (!Number.isFinite(authoredScale) || authoredScale <= EPSILON) {
            const height = new Box3().setFromObject(this.model).getSize(_a).y;
            authoredScale = height > EPSILON ? height / 1.7 : 1;
        }
        const humanMeters = tier === "course" ? 0.035 : 0.025;
        if (_towardCamera.lengthSq() <= EPSILON) return;
        const worldDelta = _c.copy(_towardCamera)
            .multiplyScalar(humanMeters * authoredScale * weight);

        hips.parent.updateWorldMatrix?.(true, false);
        _inverseParent.copy(hips.parent.matrixWorld).invert();
        _a.set(0, 0, 0).applyMatrix4(_inverseParent);
        _b.copy(worldDelta).applyMatrix4(_inverseParent).sub(_a);
        if (!finiteVector(_b)) return;

        const localDelta = _b.clone();
        hips.position.add(localDelta);
        this._contributions.push({ node: hips, rotation: null, position: localDelta });
    }

    dispose() {
        if (this._disposed) return;
        this.remove();
        this._disposed = true;
        this.viewer = null;
        this.model = null;
        this.channels = Object.create(null);
        this._headForwardLocal.set(0, 0, 0);
        this._coverage = this._emptyReport("disposed");
    }
}

export default CelebrationBodyOverlay;